import type { ThemeId } from '../catalog/platformCatalogIds';
import type { LayoutBlock, LocalBusinessBranding } from '../content/studio';
import type { PageMeta } from '../content/pageMeta';
import type { CustomColorSet } from '../theme/customColorSet';
import type { SpacingEnumKey, CornerEnumKey, TypographyEnumKey } from '../theme';

/** postMessage namespace — shell ↔ sandbox sync engine */
export const BRIDGE_CHANNEL = 'web-studio:bridge' as const;

export const BridgeMessageType = {
  ACTIVE_BLOCK: 'web-studio:active_block',
  LOCALE: 'web-studio:locale',
  WORKSPACE_MODE: 'web-studio:workspace_mode',
  BLOCKS_SYNC: 'web-studio:blocks_sync',
  CONTENT_PATCH: 'web-studio:content_patch',
  MEDIA_PATCH: 'web-studio:media_patch',
  DESIGN_TOKENS: 'web-studio:design_tokens',
} as const;

/** Studio workspace: preview-only vs full editing chrome. */
export type WorkspaceInteractionMode = 'view' | 'edit';

/** Sandbox → studio-shell (preview click-to-select, etc.) */
export const SandboxMessageType = {
  BLOCK_SELECT: 'web-studio:block_select',
  PAGE_SELECT: 'web-studio:page_select',
  LOCALE_SELECT: 'web-studio:locale_select',
  SANDBOX_READY: 'web-studio:sandbox_ready',
  SANDBOX_SYNCED: 'web-studio:sandbox_synced',
} as const;

export type SandboxMessageType = (typeof SandboxMessageType)[keyof typeof SandboxMessageType];

export type BridgeMessageType = (typeof BridgeMessageType)[keyof typeof BridgeMessageType];

export interface DesignTokensPayload {
  colors?: {
    brandPrimary?: string;
    brandAccent?: string;
  };
  fonts?: {
    brandHeading?: string;
    brandBody?: string;
  };
  spacing?: {
    brandXs?: string;
    brandSm?: string;
    brandMd?: string;
    brandLg?: string;
    brandXl?: string;
  };
}

/** Click-to-edit: localized content field in the contextual panel. */
export interface ContentFieldEditTarget {
  kind: 'content-field';
  fieldKey: string;
}

/** Click-to-edit: media URL field in the contextual panel. */
export interface MediaFieldEditTarget {
  kind: 'media-field';
  fieldKey: string;
}

/** Click-to-edit: single dish row inside menuData. */
export interface MenuItemEditTarget {
  kind: 'menu-item';
  columnIndex: number;
  itemIndex: number;
}

/** Click-to-edit: menu column header inside menuData. */
export interface MenuColumnEditTarget {
  kind: 'menu-column';
  columnIndex: number;
}

export type EditTarget =
  | ContentFieldEditTarget
  | MediaFieldEditTarget
  | MenuItemEditTarget
  | MenuColumnEditTarget;

export function editTargetsEqual(
  a: EditTarget | null | undefined,
  b: EditTarget | null | undefined,
): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return !a && !b;
  }
  if (a.kind !== b.kind) {
    return false;
  }
  switch (a.kind) {
    case 'content-field':
    case 'media-field':
      return a.fieldKey === (b as ContentFieldEditTarget | MediaFieldEditTarget).fieldKey;
    case 'menu-item':
      return (
        a.columnIndex === (b as MenuItemEditTarget).columnIndex &&
        a.itemIndex === (b as MenuItemEditTarget).itemIndex
      );
    case 'menu-column':
      return a.columnIndex === (b as MenuColumnEditTarget).columnIndex;
    default: {
      const _exhaustive: never = a;
      return _exhaustive;
    }
  }
}

export interface ActiveBlockMessage {
  type: typeof BridgeMessageType.ACTIVE_BLOCK;
  blockId: string | null;
  editTarget?: EditTarget | null;
}

export interface LocaleMessage {
  type: typeof BridgeMessageType.LOCALE;
  locale: string;
}

export interface WorkspaceModeMessage {
  type: typeof BridgeMessageType.WORKSPACE_MODE;
  mode: WorkspaceInteractionMode;
}

export interface BlocksSyncMessage {
  type: typeof BridgeMessageType.BLOCKS_SYNC;
  locale: string;
  /** Studio preview display preference; does not change saved block visibility. */
  showInvisibleBlocks?: boolean;
  /** Flat list for legacy sandbox runtime (nav + active page canvas + footer). */
  blocks: LayoutBlock[];
  /** Scoped content: active page canvas id when scoped sync is enabled. */
  activePageId?: string;
  globalBlocks?: LayoutBlock[];
  pageBlocks?: LayoutBlock[];
  themeId?: ThemeId;
  /** Registry preset id or project `custom_*` id. */
  colorsetId?: string;
  /** Project custom palettes — required when `colorsetId` is custom. */
  customColorSets?: CustomColorSet[];
  spacingEnum?: SpacingEnumKey;
  cornerEnum?: CornerEnumKey;
  typographyEnum?: TypographyEnumKey;
  /** Scoped content: page tree for nav link resolution in sandbox preview. */
  pages?: PageMeta[];
  /** Project default locale (Harbor locale switcher). */
  defaultLocale?: string;
  /** Project supported locales (Harbor locale switcher). */
  supportedLocales?: string[];
  /** Single-page vs multi-page topology for anchor nav in minimal mode. */
  siteMode?: 'minimal' | 'standard';
  /** Platform vertical — dev navigation tree selection. */
  vertical?: string;
  /**
   * Site-wide: tasting timeline present on any page (sushi Omakase nav).
   * Prefer over scanning the active-page canvas alone.
   */
  hasTastingTimeline?: boolean;
  /** PDL for sandbox-side hydrate of bound legal/footer fields. */
  personaBranding?: LocalBusinessBranding | null;
  /** Config business name fallback for PDL hydrate (`config.businessName`). */
  businessName?: string;
}

export interface ContentPatchMessage {
  type: typeof BridgeMessageType.CONTENT_PATCH;
  blockId: string;
  key: string;
  value: string;
  locale: string;
}

export interface MediaPatchMessage {
  type: typeof BridgeMessageType.MEDIA_PATCH;
  blockId: string;
  key: string;
  value: string;
}

export interface DesignTokensMessage {
  type: typeof BridgeMessageType.DESIGN_TOKENS;
  design_tokens: DesignTokensPayload;
}

export interface BlockSelectMessage {
  type: typeof SandboxMessageType.BLOCK_SELECT;
  blockId: string;
  editTarget?: EditTarget;
}

export interface PageSelectMessage {
  type: typeof SandboxMessageType.PAGE_SELECT;
  pageId: string;
  slug: string;
}

export interface LocaleSelectMessage {
  type: typeof SandboxMessageType.LOCALE_SELECT;
  locale: string;
}

export interface SandboxReadyMessage {
  type: typeof SandboxMessageType.SANDBOX_READY;
}

export interface SandboxSyncedMessage {
  type: typeof SandboxMessageType.SANDBOX_SYNCED;
  blockCount: number;
  activePageId: string | null;
}

export type ShellToSandboxMessage =
  | ActiveBlockMessage
  | LocaleMessage
  | WorkspaceModeMessage
  | BlocksSyncMessage
  | ContentPatchMessage
  | MediaPatchMessage
  | DesignTokensMessage;

export type SandboxToShellMessage =
  | BlockSelectMessage
  | PageSelectMessage
  | LocaleSelectMessage
  | SandboxReadyMessage
  | SandboxSyncedMessage;

const BRIDGE_LOCALE_KEY = /^[a-z]{2}(-[a-z0-9]{2,8})*$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function isContentPatchMessage(value: unknown): value is ContentPatchMessage {
  if (!isRecord(value)) {
    return false;
  }
  return (
    value.type === BridgeMessageType.CONTENT_PATCH &&
    typeof value.blockId === 'string' &&
    value.blockId.length > 0 &&
    typeof value.key === 'string' &&
    value.key.length > 0 &&
    typeof value.value === 'string' &&
    typeof value.locale === 'string' &&
    BRIDGE_LOCALE_KEY.test(value.locale)
  );
}

export function isMediaPatchMessage(value: unknown): value is MediaPatchMessage {
  if (!isRecord(value)) {
    return false;
  }
  return (
    value.type === BridgeMessageType.MEDIA_PATCH &&
    typeof value.blockId === 'string' &&
    value.blockId.length > 0 &&
    typeof value.key === 'string' &&
    value.key.length > 0 &&
    typeof value.value === 'string'
  );
}

export function isDesignTokensMessage(value: unknown): value is DesignTokensMessage {
  if (!isRecord(value)) {
    return false;
  }
  return (
    value.type === BridgeMessageType.DESIGN_TOKENS &&
    isRecord(value.design_tokens)
  );
}

export function isEditTarget(value: unknown): value is EditTarget {
  if (!isRecord(value) || typeof value.kind !== 'string') {
    return false;
  }
  switch (value.kind) {
    case 'content-field':
    case 'media-field':
      return typeof value.fieldKey === 'string' && value.fieldKey.length > 0;
    case 'menu-item':
      return (
        typeof value.columnIndex === 'number' &&
        Number.isInteger(value.columnIndex) &&
        value.columnIndex >= 0 &&
        typeof value.itemIndex === 'number' &&
        Number.isInteger(value.itemIndex) &&
        value.itemIndex >= 0
      );
    case 'menu-column':
      return (
        typeof value.columnIndex === 'number' &&
        Number.isInteger(value.columnIndex) &&
        value.columnIndex >= 0
      );
    default:
      return false;
  }
}

function isOptionalEditTarget(value: unknown): boolean {
  return value === undefined || value === null || isEditTarget(value);
}

function isActiveBlockMessage(value: unknown): value is ActiveBlockMessage {
  if (!isRecord(value)) {
    return false;
  }
  return (
    value.type === BridgeMessageType.ACTIVE_BLOCK &&
    (value.blockId === null || (typeof value.blockId === 'string' && value.blockId.length > 0)) &&
    isOptionalEditTarget(value.editTarget)
  );
}

function isLocaleMessage(value: unknown): value is LocaleMessage {
  if (!isRecord(value)) {
    return false;
  }
  return (
    value.type === BridgeMessageType.LOCALE &&
    typeof value.locale === 'string' &&
    BRIDGE_LOCALE_KEY.test(value.locale)
  );
}

function isWorkspaceModeMessage(value: unknown): value is WorkspaceModeMessage {
  if (!isRecord(value)) {
    return false;
  }
  return (
    value.type === BridgeMessageType.WORKSPACE_MODE &&
    (value.mode === 'view' || value.mode === 'edit')
  );
}

function isBlocksSyncMessage(value: unknown): value is BlocksSyncMessage {
  if (!isRecord(value)) {
    return false;
  }
  const hasBlocks = Array.isArray(value.blocks);
  const hasScoped =
    typeof value.activePageId === 'string' &&
    value.activePageId.length > 0 &&
    Array.isArray(value.globalBlocks) &&
    Array.isArray(value.pageBlocks);
  return (
    value.type === BridgeMessageType.BLOCKS_SYNC &&
    typeof value.locale === 'string' &&
    BRIDGE_LOCALE_KEY.test(value.locale) &&
    (value.showInvisibleBlocks === undefined ||
      typeof value.showInvisibleBlocks === 'boolean') &&
    (hasBlocks || hasScoped)
  );
}

export function isShellToSandboxMessage(value: unknown): value is ShellToSandboxMessage {
  if (!isRecord(value) || typeof value.type !== 'string') {
    return false;
  }
  switch (value.type) {
    case BridgeMessageType.ACTIVE_BLOCK:
      return isActiveBlockMessage(value);
    case BridgeMessageType.LOCALE:
      return isLocaleMessage(value);
    case BridgeMessageType.WORKSPACE_MODE:
      return isWorkspaceModeMessage(value);
    case BridgeMessageType.BLOCKS_SYNC:
      return isBlocksSyncMessage(value);
    case BridgeMessageType.CONTENT_PATCH:
      return isContentPatchMessage(value);
    case BridgeMessageType.MEDIA_PATCH:
      return isMediaPatchMessage(value);
    case BridgeMessageType.DESIGN_TOKENS:
      return isDesignTokensMessage(value);
    default:
      return false;
  }
}

function isBlockSelectMessage(value: unknown): value is BlockSelectMessage {
  if (!isRecord(value)) {
    return false;
  }
  return (
    value.type === SandboxMessageType.BLOCK_SELECT &&
    typeof value.blockId === 'string' &&
    value.blockId.length > 0 &&
    isOptionalEditTarget(value.editTarget)
  );
}

function isPageSelectMessage(value: unknown): value is PageSelectMessage {
  if (!isRecord(value)) {
    return false;
  }
  return (
    value.type === SandboxMessageType.PAGE_SELECT &&
    typeof value.pageId === 'string' &&
    value.pageId.length > 0 &&
    typeof value.slug === 'string' &&
    value.slug.length > 0
  );
}

function isLocaleSelectMessage(value: unknown): value is LocaleSelectMessage {
  if (!isRecord(value)) {
    return false;
  }
  return (
    value.type === SandboxMessageType.LOCALE_SELECT &&
    typeof value.locale === 'string' &&
    BRIDGE_LOCALE_KEY.test(value.locale)
  );
}

function isSandboxReadyMessage(value: unknown): value is SandboxReadyMessage {
  if (!isRecord(value)) {
    return false;
  }
  return value.type === SandboxMessageType.SANDBOX_READY;
}

function isSandboxSyncedMessage(value: unknown): value is SandboxSyncedMessage {
  if (!isRecord(value)) {
    return false;
  }
  return (
    value.type === SandboxMessageType.SANDBOX_SYNCED &&
    typeof value.blockCount === 'number' &&
    Number.isFinite(value.blockCount) &&
    value.blockCount >= 0 &&
    (value.activePageId === null ||
      (typeof value.activePageId === 'string' && value.activePageId.length > 0))
  );
}

export function isSandboxToShellMessage(value: unknown): value is SandboxToShellMessage {
  if (!isRecord(value) || typeof value.type !== 'string') {
    return false;
  }
  switch (value.type) {
    case SandboxMessageType.BLOCK_SELECT:
      return isBlockSelectMessage(value);
    case SandboxMessageType.PAGE_SELECT:
      return isPageSelectMessage(value);
    case SandboxMessageType.LOCALE_SELECT:
      return isLocaleSelectMessage(value);
    case SandboxMessageType.SANDBOX_READY:
      return isSandboxReadyMessage(value);
    case SandboxMessageType.SANDBOX_SYNCED:
      return isSandboxSyncedMessage(value);
    default:
      return false;
  }
}
